import apiClient from '@skygroup/shared/API/apiClient';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import endpoints from '../../API/endpoints';
import { useSelector } from 'react-redux';
import { RootState } from '../../../b2c/src/store/store';
import { PlanIdType } from '../../constants/agency';

const AUTOCOMPLETE_LIMIT = 200;

export const useDebounceText = (text: string, delay: number = 300) => {
  const [debouncedText, setDebouncedText] = useState(text);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedText(text);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [text, delay]);
  return text?.length > 1 ? debouncedText : '';
};

export const useGetCityAutoComplete = (
  cityName: string,
  currentLang: string,
  enabled: boolean = true,
  coords?: {
    latitude: number;
    longitude: number;
  }
): UseQueryResult<any, Error> => {
  const debouncedCityName = useDebounceText(cityName);

  const query = useQuery<any, Error>(
    ['city-autocomplete', debouncedCityName, coords?.latitude, coords?.longitude],
    () =>
      apiClient
        .get(
          `${endpoints.AUTOCOMPLETE.CITY}?name=${debouncedCityName}&lang=${currentLang}&limit=${AUTOCOMPLETE_LIMIT}${
            coords
              ? `&latitude=${coords.latitude}&longitude=${coords.longitude}`
              : ''
          }`
        )
        .then((response) => response.data),
    {
      keepPreviousData: false,
      enabled: !!debouncedCityName && enabled,
      staleTime: Infinity,
    }
  );

  return query;
};
// using debounce in order not to call api on each keypress!
export const useGetHotelAutoComplete = ({
  searchVal,
  currentLang,
  enable,
  coords,
  limit = 7,
  ts_id,
}: {
  searchVal: string;
  currentLang: string;
  enable?: boolean;
  coords?: {
    latitude: number;
    longitude: number;
  };
  limit?: number;
  ts_id?: string;
}): UseQueryResult<any, Error> => {
  const debouncedSearchTerm = useDebounceText(searchVal);

  const query = useQuery<any, Error>(
    // coords must be part of the key: they are what makes the response carry a
    // `distance`, so omitting them served one venue's distances for another
    [
      'hotel-autocomplete',
      debouncedSearchTerm,
      ts_id,
      coords?.latitude,
      coords?.longitude,
    ],
    async () => {
      const params = new URLSearchParams({ name: debouncedSearchTerm, language: currentLang, limit: String(limit) });
      if (coords?.latitude !== undefined) {
        params.append('latitude', String(coords.latitude));
      }
      if (coords?.longitude !== undefined) {
        params.append('longitude', String(coords.longitude));
      }
      if (ts_id) {
        params.append('ts_id', ts_id);
      }
      return apiClient
        .get(`${endpoints.AUTOCOMPLETE.HOTEL}?${params.toString()}`)
        .then((response) => response.data);
    },
    {
      keepPreviousData: false,
      enabled: enable && !!debouncedSearchTerm,
      staleTime: Infinity,
    }
  );

  return query;
};

export const useGetAirportAutoComplete = (
  airport: string,
  currentLang?: string,
  enabled: boolean = true
): UseQueryResult<any, Error> => {
  const debouncedAirport = useDebounceText(airport);

  const query = useQuery<any, Error>(
    ['airport-autocomplete', debouncedAirport],
    () =>
      apiClient
        .get(
          `${endpoints.AUTOCOMPLETE.AIRPORT}?name=${debouncedAirport}${
            currentLang ? '&lang=' + currentLang : ''
          }`
        )
        .then((response) => response.data),
    {
      keepPreviousData: false,
      enabled: !!debouncedAirport && enabled,
      staleTime: Infinity,
    }
  );

  return query;
};
export const fetchAirportByIata = async (iatacode: string) => {
  if (!iatacode) return null;
  const res = await apiClient.get(
    `${endpoints.AUTOCOMPLETE.GET_AIRPORT}?iatacode=${encodeURIComponent(
      iatacode
    )}`
  );
  return res.data;
};

export const useGetAirport = (iatacode: string): UseQueryResult<any, Error> => {
  const query = useQuery<any, Error>(
    ['get-airport', iatacode],
    () =>
      apiClient
        .get(`${endpoints.AUTOCOMPLETE.GET_AIRPORT}?iatacode=${iatacode}`)
        .then((response) => response.data),
    {
      keepPreviousData: false,
      enabled: false,
      staleTime: Infinity,
    }
  );

  return query;
};
export const useGetCity = (
  locationId: string | undefined
): UseQueryResult<any, Error> => {
  const { currentLang } = useSelector((data: RootState) => data.config);

  const query = useQuery<any, Error>(
    ['get-city', locationId],
    () =>
      apiClient
        .get(
          `${endpoints.AUTOCOMPLETE.GET_CITY}?locationId=${locationId}&lang=${currentLang}`
        )
        .then((response) => response.data),
    {
      keepPreviousData: false,
      enabled: false,
      staleTime: Infinity,
    }
  );

  return query;
};

export const useGetAutoCompleteByTS = ({
  searchString,
  tsType,
  currentLang,
  enabled = true,
  disableDebounce = false,
}: {
  searchString: string;
  tsType: string;
  currentLang?: string;
  enabled: boolean;
  disableDebounce?: boolean;
}): UseQueryResult<any, Error> => {
  const debouncedText = useDebounceText(searchString);
  const debouncedSearchString = disableDebounce ? searchString : debouncedText;

  const query = useQuery<any, Error>(
    ['autocomplete-ts', debouncedSearchString, tsType, currentLang],
    () =>
      apiClient
        .get(
          `${endpoints.AUTOCOMPLETE.GET_BY_TS}?name=${debouncedSearchString}&tsType=${tsType}&lang=${currentLang}`
        )
        .then((response) => response.data),
    {
      keepPreviousData: false,
      enabled: !!debouncedSearchString && enabled,
      staleTime: Infinity,
    }
  );

  return query;
};

export const useGetLocationsByFeature = ({
  mfeature,
}: {
  mfeature: string | null;
}): UseQueryResult<any, Error> => {
  const { agentInfo } = useSelector((data: RootState) => data.config);

  const query = useQuery<any, Error>(
    ['get-locations-by-feature', mfeature],
    () =>
      apiClient
        .get(
          `${endpoints.AUTOCOMPLETE.GET_LOCATIONS_BY_FEATURE}?mfeature=${
            !mfeature ? '' : encodeURIComponent(mfeature)
          }`
        )
        .then((response) => response.data),
    {
      keepPreviousData: false,
      enabled:
        !!mfeature &&
        agentInfo?.agency?.subscription?.plan_id !== PlanIdType.LIGHT,
      staleTime: Infinity,

      // experimental:
      //
      // generally on 'endpoints.AUTOCOMPLETE' api we dont use it
      // but i think its better to include these attributes
      refetchOnWindowFocus: false,
      retry: 2,
    }
  );

  return query;
};

export const useGetCityNameById = (
  locationId: string,
  lang?: string
): UseQueryResult<any, Error> => {
  const query = useQuery<any, Error>(
    ['get-city-name-by-id', locationId, lang],
    () =>
      apiClient
        .get(
          `${endpoints.AUTOCOMPLETE.CITY_BY_ID}?city=${locationId}${
            lang ? `&lang=${lang}` : ''
          }`
        )
        .then((response) => response.data),
    {
      keepPreviousData: false,
      enabled: false,
      staleTime: Infinity,
    }
  );

  return query;
};

export const useGetEventsAutoComplete = (
  eventName: string,
  currentLang?: string,
  enabled: boolean = true
): UseQueryResult<any, Error> => {
  const debouncedEventName = useDebounceText(eventName.toLowerCase());

  const query = useQuery<any, Error>(
    ['events-autocomplete', debouncedEventName, currentLang],
    () =>
      apiClient
        .get(
          `${
            endpoints.AUTOCOMPLETE.EVENTS
          }?name=${debouncedEventName}&limit=${AUTOCOMPLETE_LIMIT}${
            currentLang ? '&lang=' + currentLang : ''
          }`
        )
        .then((response) => {
          const responseData = response.data;
          if (responseData?.data && Array.isArray(responseData.data)) {
            const mappedData = responseData.data.map((item: any) => ({
              ...item,
              data: item.data ? JSON.parse(item.data) : item.data,
            }));

            // Group by ext_type
            const groupedData = mappedData.reduce((acc: any, item: any) => {
              const extType = item.ext_type || 'unknown';
              if (!acc[extType]) {
                acc[extType] = [];
              }
              acc[extType].push(item);
              return acc;
            }, {});

            responseData.data = groupedData;
          }
          return responseData;
        }),
    {
      keepPreviousData: false,
      enabled: !!debouncedEventName && enabled,
      staleTime: Infinity,
    }
  );

  return query;
};

/**
 * Agent-only "previous guests" autocomplete. Debounced server search over the
 * guests the agent has booked before, so the checkout / manage-order guest
 * cards can prefill.
 *
 * GET /ms/agent/previous-guests?type=<adult|child|infant>&name=<query>
 *
 * Debounced ~1.5s and skipped for a single character (see useDebounceText),
 * so we don't hit the API on every keystroke. Returns a flat array of guests
 * of the requested type (the type is already applied server-side).
 */
export const useGuestAutocomplete = ({
  searchTerm,
  type,
  enabled = true,
}: {
  searchTerm: string;
  type?: string;
  enabled?: boolean;
}): UseQueryResult<any, Error> => {
  // 1.5s debounce; useDebounceText also returns '' until 2+ chars are typed.
  const debouncedName = useDebounceText(searchTerm, 600);

  const query = useQuery<any, Error>(
    ['guest-autocomplete', debouncedName, type],
    ({ signal }) => {
      const params = new URLSearchParams();
      params.append('name', debouncedName);
      if (type) {
        params.append('type', type);
      }
      return apiClient
        .get(`${endpoints.GUEST_AUTOCOMPLETE}?${params.toString()}`, { signal })
        .then((response) => response.data.data);
    },
    {
      enabled: enabled && !!debouncedName,
      keepPreviousData: true,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  return query;
};

export const useGetEventCountries = (
  enabled: boolean = true
): UseQueryResult<any, Error> => {
  const { currentLang } = useSelector((data: RootState) => data.config);

  const query = useQuery<any, Error>(
    ['event365-countries', currentLang],
    () =>
      apiClient
        .get(
          `${endpoints.EVENT365_COUNTRIES}${
            currentLang ? '?lang=' + currentLang : ''
          }`
        )
        .then((response) => response.data),
    {
      keepPreviousData: true,
      enabled: enabled,
      staleTime: Infinity,
    }
  );

  return query;
};
